import Intercom, {
    boot as bootIntercom,
    shutdown as shutdownIntercom,
    update as updateIntercom,
} from '@intercom/messenger-js-sdk';
import { PhoneVerificationGate } from 'components/Profile/PhoneVerificationGate';
import { SpinnerOverlay } from 'components/Spinner';
import { ExternalRedirect } from 'components/ui/ExternalRedirect';
import { VenueInquiryModals } from 'components/VenueInquiryModals/VenueInquiryModals';
import { format } from 'date-fns';
import ActivateNewUser from 'pages/Login/ActivateNewUser';
import { lazy, Suspense, useEffect, useMemo, useRef } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useUserContext } from 'stores/user';
import { Column } from 'ui';
import { clearBookingOverride, getPlatformOrigin, isBookingDomain, isBookingHost } from 'utils/domain';
import { isUnauthenticatedBookingSignupRedirect } from 'utils/signupFunnelVisit';
import { AuthBlockPassed } from './BizlyPlatformRoutes';

// Lazy load large components for code splitting
const BizlyOS = lazy(() => import('./BizlyOS').then(m => ({ default: m.BizlyOS })));
const RSVP = lazy(() => import('pages/RSVP'));
const Authentication = lazy(() =>
    import('pages/Authentication/Authentication').then(m => ({ default: m.Authentication }))
);
const Login = lazy(() => import('pages/Login/Login').then(m => ({ default: m.Login })));
const DiscoverHomePage = lazy(() =>
    import('pages/Discover/DiscoverHomePage').then(m => ({ default: m.DiscoverHomePage }))
);
const DiscoverExplorePage = lazy(() =>
    import('pages/Discover/DiscoverExplorePage').then(m => ({ default: m.DiscoverExplorePage }))
);

const INTERCOM_APP_ID =
    import.meta.env.VITE_APP_ENV === 'prod'
        ? import.meta.env.VITE_APP_INTERCOM_APP_ID_PRODUCTION
        : import.meta.env.VITE_APP_INTERCOM_APP_ID;

const RedirectToSignIn = () => {
    const location = useLocation();

    const curPath = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/sign-in?redirect=${curPath}`} />;
};

// Private sign-up route - for now redirect to pricing page unless coming from specific sources
const PrivateSignUpRoute = () => {
    const location = useLocation();
    const { user } = useUserContext();
    const urlParams = new URLSearchParams(location.search);
    const referrer = urlParams.get('referrer');
    const isAuthenticated = Boolean(user.token);

    // Redirect unauthenticated B4B (Booking.com) users to the pre-experience page
    if (
        isUnauthenticatedBookingSignupRedirect({
            search: location.search,
            userLoaded: user.loaded,
            isAuthenticated,
        })
    ) {
        return <Navigate to="/discover" replace />;
    }

    // Allow sign-up only if there's a referrer query parameter
    // This makes the route "private" until we decide to make it public
    if (referrer) {
        return <Authentication page="sign-up" />;
    }

    return <ExternalRedirect to="https://www.bizly.com/" />;
};

const AuthBlocked = () => (
    <Column style={{ height: '100%' }}>
        <Suspense fallback={<SpinnerOverlay />}>
            <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/events/:eventId/rsvp" element={<RSVP />} />
                <Route path="/sign-in/*" element={<Authentication page="sign-in" />} />
                <Route path="/booking/sign-in" element={<Authentication page="sign-in" />} />
                <Route path="/sign-up/*" element={<PrivateSignUpRoute />} />
                <Route path="/discover" element={<DiscoverHomePage />} />
                <Route path="/discover/explore-venues" element={<DiscoverExplorePage />} />
                <Route path="*" element={<RedirectToSignIn />} />
            </Routes>
        </Suspense>
    </Column>
);

// Pre-login marketing surface served on the `booking.bizly.com` alias: only the Discover
// pages at clean paths; everything else falls back to the home page. Sign-in must stay
// reachable here: under the `?booking=1` QA override the platform host serves these routes,
// so without it magic-link URLs (/sign-in?token=…) dead-end at the catch-all and the token
// is never exchanged.
const BookingRoutes = () => (
    <Column style={{ height: '100%' }}>
        <Suspense fallback={<SpinnerOverlay />}>
            <Routes>
                <Route path="/" element={<DiscoverHomePage />} />
                <Route path="/explore-venues" element={<DiscoverExplorePage />} />
                <Route path="/sign-in/*" element={<Authentication page="sign-in" />} />
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </Suspense>
    </Column>
);

export const AppWithUser = () => {
    const { user } = useUserContext();
    // Read unconditionally (before the isBookingHost() early return below) so the hand-back
    // redirect can preserve the path/query — e.g. a shared venue link's `?venueId=`.
    const location = useLocation();
    const intercomSettings = useMemo(
        () =>
            user.id
                ? {
                      user_id: user.id,
                      name: user.firstName + ' ' + user.lastName,
                      email: user.email,
                      created_at: format(new Date(), 't'),
                      user_hash: user.intercomIdentityVerificationHash,
                      ...(user.team && {
                          company: {
                              id: user.team.id,
                              name: user.team.name,
                          },
                      }),
                  }
                : {},
        [user.id, user.firstName, user.lastName, user.email, user.intercomIdentityVerificationHash, user.team]
    );

    // Two refs with distinct responsibilities:
    // hasLoadedScript  — tracks whether Intercom() (the default export) has been called.
    //                    Intercom() injects the widget <script> tag exactly once; calling
    //                    it again on re-login would rely on an undocumented internal guard
    //                    rather than the intended re-boot path.
    // hasBootedIntercom — tracks whether a session is currently active. Reset on shutdown
    //                     so the next login uses boot() rather than update().
    const hasLoadedScript = useRef(false);
    const hasBootedIntercom = useRef(false);
    useEffect(() => {
        // Three distinct states:
        // !user.loaded            → spinner; never boot
        // user.loaded && !user.id → logged out (clearUser sets loaded:true, id:0);
        //                           shutdown so the previous user's session doesn't
        //                           leak to the next person on the same browser
        // user.loaded && user.id  → authenticated; boot once, then update on changes
        if (!user.loaded) {
            if (hasBootedIntercom.current) {
                shutdownIntercom();
                hasBootedIntercom.current = false;
            }
            return;
        }
        if (!user.id) {
            if (hasBootedIntercom.current) {
                shutdownIntercom();
                hasBootedIntercom.current = false;
            }
            return;
        }
        if (hasBootedIntercom.current) {
            updateIntercom(intercomSettings);
        } else if (hasLoadedScript.current) {
            // Script is already in the DOM (e.g. after logout → re-login).
            // Use the named boot() — the correct post-shutdown re-entry point.
            bootIntercom({ app_id: INTERCOM_APP_ID || '', ...intercomSettings });
            hasBootedIntercom.current = true;
        } else {
            // First ever boot: load the widget script.
            Intercom({ app_id: INTERCOM_APP_ID || '', ...intercomSettings });
            hasLoadedScript.current = true;
            hasBootedIntercom.current = true;
        }
    }, [intercomSettings, user.loaded, user.id]);

    // Unmount-only: the effect above cannot return this as its cleanup — that would
    // run before every intercomSettings change and tear the widget down between updates.
    useEffect(
        () => () => {
            if (hasBootedIntercom.current) {
                shutdownIntercom();
                hasBootedIntercom.current = false;
            }
        },
        []
    );

    if (!user.loaded) {
        return <SpinnerOverlay />;
    }

    // On the booking marketing alias, only serve the pre-login Discover pages. Authenticated
    // visitors are handed back to the main platform domain (the shared `.bizly.com` cookie
    // means they arrive already signed in).
    if (isBookingDomain()) {
        if (!user.token) {
            return <BookingRoutes />;
        }
        if (isBookingHost()) {
            // Preserve path + query (e.g. `/explore-venues?venueId=…` from a shared venue link) —
            // the booking alias's routes are a subset of the platform's own, so the same
            // pathname resolves there too. Dropping the search here is what silently swallowed
            // shared venue links for already-signed-in recipients.
            window.location.replace(`${getPlatformOrigin()}${location.pathname}${location.search}`);
            return <SpinnerOverlay />;
        }
        // `?booking=1` QA override on the platform host: the platform origin is this same host,
        // so the replace() above would reload into the still-persisted override forever. Signing
        // in IS the hand-back — drop the override and fall through to the platform routes (this
        // also preserves the magic link's post-auth redirect, which a reload would lose).
        clearBookingOverride();
    }

    return (
        <>
            <VenueInquiryModals />
            <PhoneVerificationGate />
            <Routes>
                <Route
                    path="/os/*"
                    element={
                        user.platformRole === 'planner' ? (
                            <Navigate to="/" replace />
                        ) : (
                            <Suspense fallback={<SpinnerOverlay />}>
                                <BizlyOS />
                            </Suspense>
                        )
                    }
                />
                <Route path="/accept-team-invite" element={<ActivateNewUser />} />
                <Route path="*" element={user.token ? <AuthBlockPassed /> : <AuthBlocked />} />
            </Routes>
        </>
    );
};
